// frontend/src/components/notifications/NotificationTemplateManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Save,
    RotateCcw,
    Eye,
    Globe,
    AlertCircle,
    AlertTriangle,
    CheckCircle2,
    Loader
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import { ThemedCard, ThemedButton, ThemedSelect } from '../themed';
import notificationService from '../../services/notificationService';
import MultiLanguageField from '../common/MultiLanguageField';
import ResetTemplateDialog from '../dialogs/ResetTemplateDialog';
import SaveTemplateDialog from '../dialogs/SaveTemplateDialog';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import { formatDateLocal } from '../../utils/dateTimeFormatter';
import { hasRoleLevel } from '../../utils/roleUtils';

/**
 * Notification template management component for admin users
 * Allows editing and customizing notification templates per event type and channel
 */
const NotificationTemplateManager = ({ entityId, eventTypes: manageableEventTypes = [], manageableChannels = [], userRole }) => {
    const { t } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const { entitySettings } = usePreferences(); // Entity settings drive default language and multi-language support
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [eventTypes, setEventTypes] = useState([]);
    const [enabledChannelsList, setEnabledChannelsList] = useState([]);
    const [selectedEventTypeId, setSelectedEventTypeId] = useState('');
    const [selectedChannel, setSelectedChannel] = useState('');
    const [template, setTemplate] = useState(null);
    const [editedTemplate, setEditedTemplate] = useState({
        subject: '',
        content: '',
        variables: []
    });
    const [previewMode] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [viewMode, setViewMode] = useState('custom'); // 'global' or 'custom'
    const [globalTemplate, setGlobalTemplate] = useState(null);
    const [customTemplate, setCustomTemplate] = useState(null);
    const [hasCustomTemplate, setHasCustomTemplate] = useState(false);
    const [pendingDiscardAction, setPendingDiscardAction] = useState(null);
    const [discardingChanges, setDiscardingChanges] = useState(false);

    // Use entity default language from settings
    const entityDefaultLanguage = entitySettings?.entity_default_language || 'en';
    const multiLanguageEnabled = entitySettings?.multi_language_enabled || false;

    // Check if user can edit global templates (sys_admin+ to match backend default)
    const canEditGlobalTemplates = hasRoleLevel(userRole, 'sys_admin');

    const applyTemplateToEditor = useCallback((templateData) => {
        if (!templateData) {
            setTemplate(null);
            setEditedTemplate({
                subject: '',
                content: '',
                variables: []
            });
            setHasUnsavedChanges(false);
            return;
        }

        setTemplate({ ...templateData });
        setEditedTemplate({
            subject: templateData.subject_template || '',
            content: templateData.body_template || '',
            variables: templateData.variables || []
        });
        setHasUnsavedChanges(false);
    }, []);

    useEffect(() => {
        // Initialize dropdown defaults whenever the allowed event types or channels change upstream.
        const initializeFromParent = async () => {
            try {
                setLoading(true);

                const normalizedEventTypes = manageableEventTypes || [];
                const channelNames = (manageableChannels || []).map(channel => channel.channel);

                setEventTypes(normalizedEventTypes);
                setEnabledChannelsList(channelNames);

                if (normalizedEventTypes.length === 0 || channelNames.length === 0) {
                    setSelectedEventTypeId('');
                    setSelectedChannel('');
                    return;
                }

                const defaultEventType = normalizedEventTypes[0];
                const defaultChannel = channelNames.includes('email') ? 'email' : channelNames[0];

                setSelectedEventTypeId(String(defaultEventType.id));
                setSelectedChannel(defaultChannel);

                await loadTemplateForSelections(defaultEventType, defaultChannel, entityDefaultLanguage, true);
            } catch (error) {
                console.error('Error loading template data:', error);
            } finally {
                setLoading(false);
            }
        };

        initializeFromParent();
    }, [entityId, manageableEventTypes, manageableChannels, entityDefaultLanguage]);

    /**
     * Load template for specific selections
     */
    const loadTemplateForSelections = async (eventType, channel, language, forceReload = false) => {
        try {
            const eventTypeId = typeof eventType === 'object' ? eventType.id : parseInt(eventType, 10);

            if (hasUnsavedChanges && !forceReload) {
                setPendingDiscardAction({
                    type: 'load-template',
                    eventTypeId,
                    channel,
                    language
                });
                return false;
            }

            const customData = await notificationService.getTemplate(
                entityId,
                eventTypeId,
                channel,
                {
                    language,
                    includeGlobalFallback: false
                }
            );

            const globalData = await notificationService.previewGlobalTemplate(
                entityId,
                eventTypeId,
                channel,
                language
            );

            setCustomTemplate(customData);
            setHasCustomTemplate(Boolean(customData));
            setGlobalTemplate(globalData);

            const templateForEditor = viewMode === 'global' ? globalData : customData;
            applyTemplateToEditor(templateForEditor);

            return true;
        } catch (error) {
            console.error('Error loading template:', error);
            setCustomTemplate(null);
            setGlobalTemplate(null);
            setHasCustomTemplate(false);
            applyTemplateToEditor(null);
            return false;
        }
    };

    /**
     * Show save confirmation dialog
     */
    const handleSaveTemplate = () => {
        if (!editedTemplate.content.trim()) {
            return;
        }
        setShowSaveDialog(true);
    };

    /**
     * Perform the actual save after confirmation
     */
    const performSaveTemplate = async () => {
        try {
            setSaving(true);
            setSaveStatus(null);
            setShowSaveDialog(false);

            await notificationService.saveTemplate(
                entityId,
                selectedEventTypeId,
                selectedChannel,
                {
                    subject: editedTemplate.subject,
                    body: editedTemplate.content, // Map content to body for backend
                    variables: editedTemplate.variables,
                    language: viewMode === 'global' ? 'en' : entityDefaultLanguage,
                    isGlobal: viewMode === 'global'
                }
            );

            await loadTemplateForSelections(
                selectedEventTypeId,
                selectedChannel,
                entityDefaultLanguage,
                true
            );

            setSaveStatus('success');
            setHasUnsavedChanges(false); // Reset unsaved changes flag
        } catch (error) {
            console.error('Error saving template:', error);
            setSaveStatus('error');
        } finally {
            setSaving(false);
            // Clear status after 3 seconds
            setTimeout(() => setSaveStatus(null), 3000);
        }
    };

    /**
     * Copy global template to custom template
     */
    const handleCopyFromGlobal = () => {
        if (globalTemplate) {
            setEditedTemplate({
                subject: globalTemplate.subject_template || '',
                content: globalTemplate.body_template || '',
                variables: globalTemplate.variables || []
            });
            setHasUnsavedChanges(true);
            setViewMode('custom');
        }
    };

    /**
     * Handle view mode change (global vs custom)
     */
    const applyViewModeChange = (mode) => {
        setViewMode(mode);

        if (mode === 'global') {
            applyTemplateToEditor(globalTemplate);
        } else {
            applyTemplateToEditor(customTemplate);
        }
    };

    const handleViewModeChange = (mode) => {
        if (hasUnsavedChanges && mode !== viewMode) {
            setPendingDiscardAction({
                type: 'view-mode',
                mode
            });
            return;
        }

        applyViewModeChange(mode);
    };

    const confirmDiscardChanges = async () => {
        if (!pendingDiscardAction) return;
        const action = pendingDiscardAction;

        try {
            setDiscardingChanges(true);
            setHasUnsavedChanges(false);
            setPendingDiscardAction(null);

            if (action.type === 'view-mode') {
                applyViewModeChange(action.mode);
                return;
            }

            if (action.type === 'load-template') {
                const loaded = await loadTemplateForSelections(
                    action.eventTypeId,
                    action.channel,
                    action.language,
                    true
                );

                if (loaded) {
                    setSelectedEventTypeId(String(action.eventTypeId));
                    setSelectedChannel(action.channel);
                }
            }
        } finally {
            setDiscardingChanges(false);
        }
    };

    /**
     * Convert global template to entity-specific template (Customize button)
     */
    const handleCustomizeTemplate = async () => {
        try {
            setSaving(true);
            setSaveStatus(null);

            // Copy global template to entity
            await notificationService.copyGlobalTemplateToEntity(
                entityId,
                parseInt(selectedEventTypeId),
                selectedChannel
            );

            setViewMode('custom');
            // Reload the template data to get the new entity-specific template
            await loadTemplateForSelections(
                parseInt(selectedEventTypeId),
                selectedChannel,
                entityDefaultLanguage,
                true // Force reload
            );

            setSaveStatus('success');
        } catch (error) {
            console.error('Error customizing template:', error);
            setSaveStatus('error');
        } finally {
            setSaving(false);
            // Clear status after 3 seconds
            setTimeout(() => setSaveStatus(null), 3000);
        }
    };

    /**
     * Show reset confirmation dialog
     */
    const handleResetTemplate = () => {
        if (!selectedEventTypeId || !selectedChannel) {
            return;
        }
        setShowResetDialog(true);
    };

    /**
     * Perform the actual reset after confirmation
     */
    const performResetTemplate = async () => {
        try {
            setSaving(true);
            setSaveStatus(null);
            setShowResetDialog(false);

            // Reset to global template
            await notificationService.resetTemplateToGlobal(
                entityId,
                parseInt(selectedEventTypeId),
                selectedChannel
            );

            // Load the global template that will now be used
            await loadTemplateForSelections(
                parseInt(selectedEventTypeId),
                selectedChannel,
                entityDefaultLanguage,
                true // Force reload
            );

            setSaveStatus('success');
            setHasUnsavedChanges(false);

            // Success feedback handled by save status indicator
        } catch (error) {
            console.error('Error resetting template to global:', error);
            setSaveStatus('error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-32", text.secondary)}>
                <Loader className="w-6 h-6 animate-spin mr-3" />
                <span>{t('loadingTemplates')}...</span>
            </div>
        );
    }

    if (enabledChannelsList.length === 0) {
        return (
            <ThemedCard className="p-8">
                <div className={cn("text-center", text.secondary)}>
                    <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h3 className={cn("text-lg font-medium mb-2", text.primary)}>
                        {t('noChannelsConfigured')}
                    </h3>
                    <p>
                        {manageableChannels.length === 0
                            ? t('insufficientRoleForChannel')
                            : t('configureChannelsBeforeManagingTemplates')}
                    </p>
                </div>
            </ThemedCard>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className={cn("text-lg font-semibold", text.primary)}>
                    {t('templateManagement')}
                </h2>
                <p className={cn("text-sm", text.secondary)}>
                    {t('customizeNotificationTemplatesForYourOrganization')}
                </p>
            </div>

            {/* Template Selection */}
            <ThemedCard className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    {/* Event Type Selection */}
                    <div>
                        <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                            {t('eventType')}
                        </label>
                        <ThemedSelect
                            value={selectedEventTypeId}
                            placeholder={t('selectEventType')}
                            options={eventTypes.map(eventType => ({
                                value: String(eventType.id),
                                label: t(eventType.display_name_key) || eventType.display_name_key
                            }))}
                            onChange={async (e) => {
                                const eventId = e.target.value;
                                const eventType = eventTypes.find(et => String(et.id) === eventId);
                                if (eventType && selectedChannel) {
                                    const loaded = await loadTemplateForSelections(eventType, selectedChannel, entityDefaultLanguage);
                                    if (loaded) {
                                        setSelectedEventTypeId(eventId);
                                    }
                                } else {
                                    setSelectedEventTypeId(eventId);
                                }
                            }}
                        />
                    </div>

                    {/* Channel Selection */}
                    <div>
                        <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                            {t('channel')}
                        </label>
                        <ThemedSelect
                            value={selectedChannel}
                            placeholder={t('selectChannel')}
                            options={enabledChannelsList.map(channel => ({
                                value: channel,
                                label: t(`${channel}Channel`) || channel
                            }))}
                            onChange={async (e) => {
                                const channel = e.target.value;
                                if (selectedEventTypeId && channel) {
                                    const loaded = await loadTemplateForSelections(selectedEventTypeId, channel, entityDefaultLanguage);
                                    if (loaded) {
                                        setSelectedChannel(channel);
                                    }
                                } else {
                                    setSelectedChannel(channel);
                                }
                            }}
                        />
                    </div>

                    {/* Default Language Display */}
                    <div>
                        <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                            <Globe className="w-4 h-4 inline mr-1" />
                            {t('entityDefaultLanguage')}
                        </label>
                        <div className={cn(
                            "px-3 py-2 border rounded-md",
                            background.secondary,
                            text.primary,
                            border.primary
                        )}>
                            {entityDefaultLanguage.toUpperCase()}
                        </div>
                    </div>

                    {/* Template View Mode */}
                    <div>
                        <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                            <Eye className="w-4 h-4 inline mr-1" />
                            {t('templateView')}
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="viewMode"
                                    value="global"
                                    checked={viewMode === 'global'}
                                    onChange={(e) => handleViewModeChange(e.target.value)}
                                    className="mr-2"
                                />
                                <span className={cn("text-sm", text.primary)}>{t('globalTemplate')}</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="viewMode"
                                    value="custom"
                                    checked={viewMode === 'custom'}
                                    onChange={(e) => handleViewModeChange(e.target.value)}
                                    className="mr-2"
                                />
                                <span className={cn("text-sm", text.primary)}>{t('customTemplate')}</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Template Status */}
                {template && (
                    <div className={cn("p-3 rounded-md mb-4", background.secondary)}>
                        <p className={cn("text-sm", text.secondary)}>
                            {template.is_default 
                                ? t('usingDefaultTemplate') 
                                : t('usingCustomTemplate')
                            }
                            {template.updated_at && (
                                <span className="ml-2">
                                    {t('lastModified')}: {formatDateLocal(template.updated_at)}
                                </span>
                            )}
                        </p>
                    </div>
                )}
            </ThemedCard>

            {/* Template Editor */}
            <ThemedCard className="p-6">
                {viewMode === 'custom' && !hasCustomTemplate && (
                    <div className="mb-4 p-3 rounded-md bg-yellow-50 border border-yellow-200">
                        <p className="text-sm text-yellow-800">
                            {t('No custom template created yet')}
                        </p>
                    </div>
                )}
                {/* Template Status Indicator */}
                {template && (
                    <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Globe className="w-4 h-4 mr-2 text-blue-600" />
                                <span className="text-sm text-blue-800">
                                    {template.isGlobal ?
                                        t('usingGlobalTemplate') || 'Using Global Template' :
                                        t('usingCustomTemplate') || 'Using Custom Template'
                                    }
                                </span>
                            </div>
                            {template.isGlobal && (
                                <ThemedButton
                                    onClick={handleCustomizeTemplate}
                                    variant="outline"
                                    size="sm"
                                    disabled={saving}
                                >
                                    {saving ? t('customizing') : (t('customizeTemplate') || 'Customize')}
                                </ThemedButton>
                            )}
                        </div>
                    </div>
                )}

                <div className="space-y-4">
                    {/* Subject Line (Email only) */}
                    {selectedChannel === 'email' && !previewMode && (
                        multiLanguageEnabled && template?.id ? (
                            <MultiLanguageField
                                entityId={viewMode === 'global' ? null : entityId}
                                entityDefaultLanguage={viewMode === 'global' ? 'en' : entityDefaultLanguage}
                                multiLanguageEnabled={multiLanguageEnabled}
                                contentType="notification_template"
                                contentId={template.id}
                                fieldName="subject_template"
                                defaultValue={editedTemplate.subject}
                                onChange={(fieldName, value, isDefault) => {
                                    if (isDefault && (viewMode === 'custom' || canEditGlobalTemplates)) {
                                        setEditedTemplate(prev => ({ ...prev, subject: value }));
                                        setHasUnsavedChanges(true);
                                    }
                                }}
                                label={t('emailSubject')}
                                required={true}
                                multiline={false}
                                disabled={viewMode === 'global' && !canEditGlobalTemplates}
                                isGlobal={viewMode === 'global'}
                                eventTypeId={viewMode === 'global' ? parseInt(selectedEventTypeId, 10) : null}
                                channel={viewMode === 'global' ? selectedChannel : null}
                            />
                        ) : (
                            <div>
                                <label className={cn("block text-sm font-medium mb-2", text.primary)}>
                                    {t('emailSubject')}
                                </label>
                                <input
                                    type="text"
                                    value={editedTemplate.subject}
                                    onChange={(e) => {
                                        setEditedTemplate(prev => ({ ...prev, subject: e.target.value }));
                                        setHasUnsavedChanges(true);
                                    }}
                                    className={cn(
                                        "w-full px-3 py-2 border rounded-md",
                                        "focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                                        background.primary,
                                        text.primary,
                                        border.primary
                                    )}
                                    placeholder={t('enterEmailSubject')}
                                />
                            </div>
                        )
                    )}

                    {/* Content */}
                    <div>
                        {viewMode === 'global' ? (
                            // Global Template View
                            <div>
                                {multiLanguageEnabled && globalTemplate?.id ? (
                                    <MultiLanguageField
                                        entityId={null}
                                        entityDefaultLanguage="en"
                                        multiLanguageEnabled={multiLanguageEnabled}
                                        contentType="notification_template"
                                        contentId={globalTemplate.id}
                                        fieldName="body_template"
                                        defaultValue={editedTemplate.content}
                                        onChange={(fieldName, value, isDefault) => {
                                            if (isDefault && canEditGlobalTemplates) {
                                                setEditedTemplate(prev => ({ ...prev, content: value }));
                                                setHasUnsavedChanges(true);
                                            }
                                        }}
                                        label={selectedChannel === 'email' ? t('emailContent') : t('messageContent')}
                                        required={true}
                                        multiline={true}
                                        disabled={!canEditGlobalTemplates}
                                        isGlobal={true}
                                        eventTypeId={parseInt(selectedEventTypeId, 10)}
                                        channel={selectedChannel}
                                    />
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className={cn("block text-sm font-medium", text.primary)}>
                                                {selectedChannel === 'email' ? t('emailContent') : t('messageContent')}
                                                <span className="text-xs text-blue-500 ml-2">({t('globalTemplate')})</span>
                                            </label>
                                        </div>
                                        {globalTemplate ? (
                                            <div className={cn("p-4 border rounded-md whitespace-pre-wrap", background.secondary, border.primary, text.primary)}>
                                                {globalTemplate.body_template || t('No global template available')}
                                            </div>
                                        ) : (
                                            <div className={cn("p-4 border rounded-md text-center", background.secondary, border.primary, text.secondary)}>
                                                {t('No global template available')}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            // Custom Template View (Editable)
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className={cn("block text-sm font-medium", text.primary)}>
                                        {selectedChannel === 'email' ? t('emailContent') : t('messageContent')}
                                        <span className="text-xs text-green-500 ml-2">({t('customTemplate')})</span>
                                    </label>
                                    {!editedTemplate.content.trim() && globalTemplate && (
                                        <ThemedButton
                                            onClick={handleCopyFromGlobal}
                                            variant="outline"
                                            size="sm"
                                        >
                                            <Eye className="w-4 h-4 mr-1" />
                                            {t('copyFromGlobal')}
                                        </ThemedButton>
                                    )}
                                </div>

                                {multiLanguageEnabled && template?.id ? (
                                    <MultiLanguageField
                                        entityId={viewMode === 'global' ? null : entityId}
                                        entityDefaultLanguage={viewMode === 'global' ? 'en' : entityDefaultLanguage}
                                        multiLanguageEnabled={multiLanguageEnabled}
                                        contentType="notification_template"
                                        contentId={template.id}
                                        fieldName="body_template"
                                        defaultValue={editedTemplate.content}
                                        onChange={(fieldName, value, isDefault) => {
                                            if (isDefault) {
                                                setEditedTemplate(prev => ({ ...prev, content: value }));
                                                setHasUnsavedChanges(true);
                                            }
                                        }}
                                        label={selectedChannel === 'email' ? t('emailContent') : t('messageContent')}
                                        required={true}
                                        multiline={true}
                                        isGlobal={viewMode === 'global'}
                                        eventTypeId={viewMode === 'global' ? parseInt(selectedEventTypeId, 10) : null}
                                        channel={viewMode === 'global' ? selectedChannel : null}
                                    />
                                ) : (
                                    <>
                                        {!editedTemplate.content.trim() ? (
                                            <div className={cn("p-8 border-2 border-dashed rounded-md text-center", border.primary, text.secondary)}>
                                                <div className="space-y-3">
                                                    <div className="text-lg">📝</div>
                                                    <p>{t('No custom template created yet')}</p>
                                                    {globalTemplate && (
                                                        <ThemedButton
                                                            onClick={handleCopyFromGlobal}
                                                            variant="primary"
                                                            size="sm"
                                                        >
                                                            <Eye className="w-4 h-4 mr-1" />
                                                            {t('copyFromGlobal')}
                                                        </ThemedButton>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <textarea
                                                value={editedTemplate.content}
                                                onChange={(e) => {
                                                    setEditedTemplate(prev => ({ ...prev, content: e.target.value }));
                                                    setHasUnsavedChanges(true);
                                                }}
                                                rows={selectedChannel === 'sms' ? 4 : 8}
                                                placeholder={t('enterTemplateContent')}
                                                className={cn(
                                                    "w-full px-3 py-2 border rounded-md font-mono",
                                                    "focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                                                    "resize-vertical",
                                                    background.primary,
                                                    text.primary,
                                                    border.primary
                                                )}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Available Variables */}
                    {viewMode === 'custom' && (
                        <div>
                            <h4 className={cn("text-sm font-medium mb-2", text.primary)}>
                                {t('availableVariables')}
                            </h4>
                            <div className={cn("p-3 rounded-md", background.secondary)}>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                    {(template?.variables || []).map(variable => (
                                        <code
                                            key={variable}
                                            className={cn(
                                                "px-2 py-1 rounded cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900",
                                                text.secondary
                                            )}
                                            onClick={() => {
                                                const textarea = document.querySelector('textarea');
                                                if (textarea) {
                                                    const start = textarea.selectionStart;
                                                    const end = textarea.selectionEnd;
                                                    const newContent =
                                                        editedTemplate.content.substring(0, start) +
                                                        `{{${variable}}}` +
                                                        editedTemplate.content.substring(end);
                                                    setEditedTemplate(prev => ({ ...prev, content: newContent }));
                                                    setHasUnsavedChanges(true);
                                                }
                                            }}
                                        >
                                            {`{{${variable}}}`}
                                        </code>
                                    ))}
                                </div>
                                <p className={cn("text-xs mt-2", text.secondary)}>
                                    {t('clickVariableToInsert')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-between items-center pt-4">
                        <div className="flex space-x-3">
                            {viewMode === 'custom' && (
                                <ThemedButton
                                    onClick={handleResetTemplate}
                                    variant="outline"
                                    disabled={saving}
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    {t('resetToGlobal') || 'Reset to Global'}
                                </ThemedButton>
                            )}
                        </div>

                        <div className="flex items-center space-x-3">
                            {/* Save Status */}
                            {saveStatus && (
                                <div className="flex items-center">
                                    {saveStatus === 'success' ? (
                                        <CheckCircle2 className="w-4 h-4 text-green-600 mr-1" />
                                    ) : (
                                        <AlertCircle className="w-4 h-4 text-red-600 mr-1" />
                                    )}
                                    <span className={cn(
                                        "text-sm",
                                        saveStatus === 'success' ? 'text-green-600' : 'text-red-600'
                                    )}>
                                        {saveStatus === 'success' ? t('templateSaved') : t('errorSavingTemplate')}
                                    </span>
                                </div>
                            )}

                            {(viewMode === 'custom' || (viewMode === 'global' && canEditGlobalTemplates)) && (
                                <ThemedButton
                                    onClick={handleSaveTemplate}
                                    disabled={saving || (!editedTemplate.content.trim())}
                                >
                                    {saving ? (
                                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    {saving ? t('saving') : t('saveTemplate')}
                                </ThemedButton>
                            )}
                        </div>
                    </div>
                </div>
            </ThemedCard>

            {/* Template Guidelines */}
            <ThemedCard className="p-6">
                <h3 className={cn("text-md font-medium mb-3", text.primary)}>
                    {t('templateGuidelines')}
                </h3>
                <div className="space-y-2 text-sm">
                    <div className={cn("flex items-start", text.secondary)}>
                        <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('useVariablesForDynamicContent')}</span>
                    </div>
                    <div className={cn("flex items-start", text.secondary)}>
                        <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>
                            {selectedChannel === 'sms' 
                                ? t('smsLengthLimit') 
                                : t('keepEmailContentConcise')
                            }
                        </span>
                    </div>
                    <div className={cn("flex items-start", text.secondary)}>
                        <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{t('testTemplatesBeforeDeployment')}</span>
                    </div>
                </div>
            </ThemedCard>

            {/* Save Template Confirmation Dialog */}
            <SaveTemplateDialog
                isOpen={showSaveDialog}
                onClose={() => setShowSaveDialog(false)}
                onConfirm={performSaveTemplate}
                isSaving={saving}
                eventType={eventTypes.find(et => String(et.id) === selectedEventTypeId)?.display_name_key}
                channel={selectedChannel}
                hasChanges={hasUnsavedChanges}
            />

            {/* Reset Template Confirmation Dialog */}
            <ResetTemplateDialog
                isOpen={showResetDialog}
                onClose={() => setShowResetDialog(false)}
                onConfirm={performResetTemplate}
                isResetting={saving}
                templateType={`${selectedChannel} template`}
            />

            <ConfirmDialogShell
                isOpen={!!pendingDiscardAction}
                onClose={() => !discardingChanges && setPendingDiscardAction(null)}
                onConfirm={confirmDiscardChanges}
                title={t('unsavedChanges', { defaultValue: 'Unsaved changes' })}
                confirmLabel={t('discardChanges', { defaultValue: 'Discard Changes' })}
                confirmVariant="warning"
                confirmIcon={AlertTriangle}
                isLoading={discardingChanges}
                isDisabled={discardingChanges}
                showIcon={true}
                iconVariant="warning"
                closeOnBackdropClick={!discardingChanges}
            >
                <p className={cn("text-sm", text.secondary)}>
                    {t('You have unsaved changes. Do you want to discard them?', {
                        defaultValue: 'You have unsaved changes. Do you want to discard them?'
                    })}
                </p>
            </ConfirmDialogShell>
        </div>
    );
};

export default NotificationTemplateManager;
